import React from 'react';
import s from './Pagination.scss';
import {ArrowLeft, ArrowRight} from '../../../icons/dist';
import classNames from 'classnames';
import {useIsAfterHydration} from '../hooks/useIsAfterHydration';
import {useExperiments} from '@wix/yoshi-flow-editor';
import {SPECS} from '../../../specs';
import {LayoutId} from '../../../constants';

export interface PaginationProps {
  prev: PaginationItem;
  next: PaginationItem;
  isRTL: boolean;
  shouldRenderAfterHydration: boolean;
  layoutId: LayoutId;
}

export interface PaginationItem {
  text: string;
  href?: string;
  onClick?(event): void;
}

export const enum PaginationDataHook {
  Pagination = 'pagination',
  HeightPlaceholder = 'pagination.height-placeholder',
}

export const Pagination: React.FC<PaginationProps> = ({prev, next, isRTL, shouldRenderAfterHydration, layoutId}) => {
  const {isAfterHydration} = useIsAfterHydration();
  const {experiments} = useExperiments();
  const shouldRemovePagination =
    experiments.enabled(SPECS.ProductPageRemovePagination) && layoutId === LayoutId.Responsive;
  const shouldRenderPagination =
    !shouldRemovePagination &&
    (((shouldRenderAfterHydration ? /* istanbul ignore next: hard to test */ isAfterHydration : true) && next.href) ||
      prev.href);

  const renderLink = (item: PaginationItem, rel: 'prev' | 'next') => {
    const {href, onClick} = item;

    /* eslint-disable react/jsx-no-useless-fragment */
    const content = (
      <>
        {rel === 'prev' ? (
          <>
            <ArrowLeft />
            {item.text}
          </>
        ) : (
          <>
            {item.text}
            <ArrowRight />
          </>
        )}
      </>
    );

    return (
      <a rel={rel} href={href} className={classNames(s[rel], {[s.rtl]: isRTL})} onClick={onClick}>
        {content}
      </a>
    );
  };

  const FullState = () => (
    <>
      {renderLink(prev, 'prev')}
      {renderLink(next, 'next')}
    </>
  );

  const HeightPlaceholder = () => (
    <div className={s.hidden} data-hook={PaginationDataHook.HeightPlaceholder}>
      <ArrowLeft />
    </div>
  );

  return (
    <div data-hook={PaginationDataHook.Pagination} className={s.pagination}>
      {shouldRenderPagination ? <FullState /> : <HeightPlaceholder />}
    </div>
  );
};
